import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Chat, Channel, MessageComposer, MessageList, ModalContextProvider,
  Thread, Window, WithComponents,
} from 'stream-chat-react';
import { EmojiPicker } from 'stream-chat-react/emojis';
import { init, SearchIndex } from 'emoji-mart';
import data from '@emoji-mart/data';
import { useStreamChat } from '../contexts/StreamChatContext';
import { Loader, Palette, ArrowLeft, Maximize2, Minimize2 } from 'lucide-react';
import { apiCall } from '../../services/apiUtil';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';
import 'stream-chat-react/dist/css/index.css';
import { safeAreaTop } from '../../utils/helpers';

// Initialise emoji-mart data once at module load
init({ data });

const _syncing = new Set();
const _lastSynced = new Map();

// Replaces GlobalModal (position:fixed) so the poll/attachment dialog stays
// inside the channel container instead of covering the whole page.
const AbsoluteModal = ({ open, onClose, children, className }) => {
  if (!open) return null;
  return (
    <div
      className={`str-chat__modal str-chat__modal--open${className ? ' ' + className : ''}`}
      style={{ position: 'absolute', inset: 0, zIndex: 500, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      onTouchMove={(e) => {
        // Block background scroll only when touching the backdrop itself.
        // When touching inside the dialog, let the event through so the
        // dialog body can scroll naturally on iOS.
        if (e.target === e.currentTarget) e.stopPropagation();
      }}
    >
      <ModalContextProvider value={{ close: () => onClose?.() }}>
        {children}
      </ModalContextProvider>
    </div>
  );
};

// Stream passes either a pixel number or a named token ("xs"|"sm"|"md"|"lg"|"xl"|"2xl")
// as `size`, depending on call site. Pixel values match Stream's own avatar CSS.
const AVATAR_SIZE_TOKENS = { xs: 20, sm: 24, md: 32, lg: 40, xl: 48, '2xl': 64 };

// Custom Avatar that resolves relative /images/... paths to absolute URLs on mobile.
// The Stream SDK stores profile images as relative paths; on Capacitor they'd resolve
// to capacitor://localhost/images/... (404). resolveImageUrl prepends the backend domain.
const StreamAvatar = ({ image, name, size = 32 }) => {
  const px = AVATAR_SIZE_TOKENS[size] ?? size;
  const src = image ? resolveImageUrl(image) : null;
  const initials = name ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';
  return (
    <div
      className="str-chat__avatar str-chat__avatar--rounded"
      style={{ width: px, height: px, flexShrink: 0 }}
      title={name}
    >
      {src
        ? <img className="str-chat__avatar-image" src={src} alt={name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} onError={e => { e.currentTarget.style.display = 'none'; }} />
        : <div className="str-chat__avatar-fallback" style={{ width: '100%', height: '100%', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: px * 0.4 }}>{initials}</div>
      }
    </div>
  );
};

// Modal override keeps poll/attachment dialog contained inside the channel container
const CHAT_OVERRIDES = { Modal: AbsoluteModal, Avatar: StreamAvatar };

// ---------------------------------------------------------------------------
// Theme definitions
// ---------------------------------------------------------------------------
const PALETTES = {
  blue: {
    50: '#f3f7ff', 100: '#e3edff', 150: '#c3d9ff', 200: '#a5c5ff',
    300: '#78a8ff', 400: '#4586ff', 500: '#005fff', 600: '#1b53bd',
    700: '#19418d', 800: '#142f63', 900: '#091a3b',
  },
  green: {
    50: '#f0fdf4', 100: '#dcfce7', 150: '#bbf7d0', 200: '#86efac',
    300: '#4ade80', 400: '#22c55e', 500: '#16a34a', 600: '#15803d',
    700: '#166534', 800: '#14532d', 900: '#052e16',
  },
  purple: {
    50: '#f5f3ff', 100: '#ede9fe', 150: '#ddd6fe', 200: '#c4b5fd',
    300: '#a78bfa', 400: '#8b5cf6', 500: '#7c3aed', 600: '#6d28d9',
    700: '#5b21b6', 800: '#4c1d95', 900: '#2e1065',
  },
  orange: {
    50: '#fffbeb', 100: '#fef3c7', 150: '#fde68a', 200: '#fcd34d',
    300: '#fbbf24', 400: '#f59e0b', 500: '#d97706', 600: '#b45309',
    700: '#92400e', 800: '#78350f', 900: '#451a03',
  },
  teal: {
    50: '#f0fdfa', 100: '#ccfbf1', 150: '#99f6e4', 200: '#5eead4',
    300: '#2dd4bf', 400: '#14b8a6', 500: '#0d9488', 600: '#0f766e',
    700: '#115e59', 800: '#134e4a', 900: '#042f2e',
  },
  rose: {
    50: '#fff1f2', 100: '#ffe4e6', 150: '#fecdd3', 200: '#fda4af',
    300: '#fb7185', 400: '#f43f5e', 500: '#e11d48', 600: '#be123c',
    700: '#9f1239', 800: '#881337', 900: '#4c0519',
  },
};

// Build a scoped CSS rule that overrides the brand palette on the .str-chat
// element.  .scopeClass.str-chat (0,2,0) beats Stream's .str-chat (0,1,0).
const makeScopedStyle = (scopeClass, paletteName) => {
  const p = PALETTES[paletteName];
  const vars = [
    `--str-chat__brand-50:${p[50]}`,
    `--str-chat__brand-100:${p[100]}`,
    `--str-chat__brand-150:${p[150]}`,
    `--str-chat__brand-200:${p[200]}`,
    `--str-chat__brand-300:${p[300]}`,
    `--str-chat__brand-400:${p[400]}`,
    `--str-chat__brand-500:${p[500]}`,
    `--str-chat__brand-600:${p[600]}`,
    `--str-chat__brand-700:${p[700]}`,
    `--str-chat__brand-800:${p[800]}`,
    `--str-chat__brand-900:${p[900]}`,
  ].join(';');
  return `
.${scopeClass}.str-chat{${vars}}
.${scopeClass}.str-chat,
.${scopeClass}.str-chat .str-chat__channel,
.${scopeClass}.str-chat .str-chat__container,
.${scopeClass}.str-chat .str-chat__main-panel,
.${scopeClass}.str-chat .str-chat__main-panel-inner {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}
.${scopeClass}.str-chat .str-chat__message-list-scroll,
.${scopeClass}.str-chat .str-chat__message-input {
  max-width: 100% !important;
  overflow-x: hidden !important;
}
`;
};

// Scoped layout fixes (reactions position + poll dialog containment).
// Same two-class specificity (.sc-X.str-chat) beats SDK's single-class rules.
const makeScopedLayoutStyle = (scopeClass) => `
.${scopeClass}.str-chat .str-chat__message .str-chat__message-inner {
  grid-template-areas: "message-bubble options" "replies replies" "reactions ." !important;
}
.${scopeClass}.str-chat .str-chat__message.str-chat__message--me .str-chat__message-inner {
  grid-template-areas: "reminder reminder" "options message-bubble" "replies replies" ". reactions" !important;
}
.${scopeClass}.str-chat .str-chat__poll-creation-dialog {
  height: min(460px, 100%) !important;
  max-height: 100% !important;
  min-height: unset !important;
  overflow: hidden !important;
}
@media (max-width: 768px) {
  .${scopeClass}.str-chat .str-chat__poll-creation-dialog {
    width: calc(100vw - 32px) !important;
    margin: 0 16px !important;
  }
}
.${scopeClass}.str-chat .str-chat__poll-creation-dialog .str-chat__prompt {
  flex: 1 1 0 !important;
  min-height: 0 !important;
  height: 100% !important;
  max-height: 100% !important;
  position: relative !important;
  display: flex !important;
  flex-direction: column !important;
  overflow: hidden !important;
}
.${scopeClass}.str-chat .str-chat__poll-creation-dialog .str-chat__prompt__body {
  max-height: 280px !important;
  overflow-y: auto !important;
  -webkit-overflow-scrolling: touch !important;
  overscroll-behavior: contain !important;
}
.${scopeClass}.str-chat .str-chat__poll-creation-dialog .str-chat__prompt__footer {
  position: absolute !important;
  top: 0 !important;
  right: calc(var(--str-chat__spacing-xl) + var(--str-chat__size-32) + var(--str-chat__spacing-xs)) !important;
  bottom: auto !important;
  width: auto !important;
  padding: 0 !important;
  background: transparent !important;
  border: none !important;
  height: var(--str-chat__size-32) !important;
  margin-top: var(--str-chat__spacing-xl) !important;
  display: flex !important;
  align-items: center !important;
}
.${scopeClass}.str-chat .str-chat__poll-creation-dialog .str-chat__prompt__footer__controls-button--cancel {
  display: none !important;
}
`;

const STREAM_THEMES = [
  { id: 'light',        label: 'Light',        base: 'messaging str-chat__theme-light',   palette: 'blue'   },
  { id: 'dark',         label: 'Dark',         base: 'messaging str-chat__theme-dark',    palette: 'blue'   },
  { id: 'inverse',      label: 'Inverse',      base: 'messaging str-chat__theme-inverse', palette: 'blue'   },
  { id: 'light-green',  label: 'Light Green',  base: 'messaging str-chat__theme-light',   palette: 'green'  },
  { id: 'dark-green',   label: 'Dark Green',   base: 'messaging str-chat__theme-dark',    palette: 'green'  },
  { id: 'light-purple', label: 'Light Purple', base: 'messaging str-chat__theme-light',   palette: 'purple' },
  { id: 'dark-purple',  label: 'Dark Purple',  base: 'messaging str-chat__theme-dark',    palette: 'purple' },
  { id: 'light-orange', label: 'Light Orange', base: 'messaging str-chat__theme-light',   palette: 'orange' },
  { id: 'dark-orange',  label: 'Dark Orange',  base: 'messaging str-chat__theme-dark',    palette: 'orange' },
  { id: 'light-teal',   label: 'Light Teal',   base: 'messaging str-chat__theme-light',   palette: 'teal'   },
  { id: 'dark-teal',    label: 'Dark Teal',    base: 'messaging str-chat__theme-dark',    palette: 'teal'   },
  { id: 'light-rose',   label: 'Light Rose',   base: 'messaging str-chat__theme-light',   palette: 'rose'   },
  { id: 'dark-rose',    label: 'Dark Rose',    base: 'messaging str-chat__theme-dark',    palette: 'rose'   },
];

// ---------------------------------------------------------------------------
// Theme picker dropdown
// ---------------------------------------------------------------------------
const ThemePicker = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const current = STREAM_THEMES.find(t => t.id === selected) ?? STREAM_THEMES[0];
  const swatch = PALETTES[current.palette][500];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        title="Change chat theme"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 10px',
          background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 20, cursor: 'pointer', color: '#fff', fontSize: 12,
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ width: 10, height: 10, borderRadius: '50%', background: swatch, flexShrink: 0 }} />
        <Palette size={13} />
        <span>{current.label}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0,
          background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: 10, padding: '6px 0', minWidth: 160,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          zIndex: 300, maxHeight: 340, overflowY: 'auto',
        }}>
          {STREAM_THEMES.map(t => (
            <button
              key={t.id}
              onClick={() => { onChange(t.id); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '7px 14px',
                background: t.id === selected ? 'rgba(255,255,255,0.1)' : 'transparent',
                border: 'none', cursor: 'pointer', color: '#fff', fontSize: 13,
                textAlign: 'left',
              }}
            >
              <span style={{ width: 12, height: 12, borderRadius: '50%', background: PALETTES[t.palette][500], flexShrink: 0 }} />
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
//
// Two usage modes:
//   1. channel prop provided  — skips init/sync, renders the given channel directly
//   2. channelId + members    — syncs members server-side then watches the channel
// ---------------------------------------------------------------------------
const StreamChatComponent = ({
  // Mode 1: pre-established channel
  channel: channelProp = null,
  // Mode 2: create/sync from props
  channelId,
  channelName,
  channelType = 'gaming',
  creatorId,
  members,
  // Shared options
  readOnly = false,
  readOnlyMessage = 'Messaging is not available.',
  height = 600,
  // Leaves the conversation entirely — the host shows its list again. Hosts
  // without a list (a squad's Chat tab is the only chat there is) omit it, and
  // the back control falls back to just leaving full screen.
  onBack = null,
  // A host that owns full screen itself (MessagesPage does, so that Back can
  // show the conversation list without un-zooming) turns this off; otherwise
  // there would be two overlays and two competing expand buttons.
  allowFullscreen = true,
  // Fill the host's box rather than the fixed `height` — used when the host has
  // put us inside its own full-screen shell.
  fillParent = false,
}) => {
  const { chatClient, isConnecting, error, connectChat } = useStreamChat();
  const navigate = useNavigate();

  // Chat connects lazily — only once something actually needs it (this
  // component, rendered by the Messages page and squad Chat tabs).
  useEffect(() => {
    connectChat();
  }, [connectChat]);

  const [themeId, setThemeId] = useState(STREAM_THEMES[0].id);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const activeTheme = STREAM_THEMES.find(t => t.id === themeId) ?? STREAM_THEMES[0];
  const isDark = activeTheme.base.includes('dark');
  const headerBg = isDark ? '#1a1a2e' : '#ffffff';
  const headerText = isDark ? '#f9fafb' : '#111827';
  const headerBorder = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';

  const scopeClass = useMemo(
    () => 'sc-' + String(channelProp?.id || channelId || 'chat').replace(/[^a-z0-9]/gi, ''),
    [channelProp?.id, channelId],
  );
  const scopedStyle = useMemo(
    () => makeScopedStyle(scopeClass, activeTheme.palette),
    [scopeClass, activeTheme.palette],
  );
  const scopedLayoutStyle = useMemo(() => makeScopedLayoutStyle(scopeClass), [scopeClass]);
  const customClasses = useMemo(() => ({ chat: `str-chat ${scopeClass}` }), [scopeClass]);

  const [channel, setChannel] = useState(channelProp);
  const [isLoadingChannel, setIsLoadingChannel] = useState(!channelProp);
  const channelRef = useRef(null);

  const memberIdsKey = useMemo(() => {
    if (!members || !Array.isArray(members)) return '';
    return members.map(m => String(m.userId)).sort().join(',');
  }, [members]);

  const getMemberIds = () => members?.map(m => String(m.userId)) ?? [];

  const syncMembersToChannel = async (memberIds) => {
    const userDetails = await apiCall('/stream/get-user-details', {
      method: 'POST',
      body: JSON.stringify({ userIds: memberIds }),
    });
    if (!userDetails.success) throw new Error('Failed to fetch user details');

    await apiCall('/stream/upsert-users', {
      method: 'POST',
      body: JSON.stringify({ users: userDetails.users }),
    });

    await apiCall('/stream/create-channel', {
      method: 'POST',
      body: JSON.stringify({
        channelType,
        channelId,
        channelData: channelName ? { name: channelName } : {},
        members: memberIds,
        creatorId: creatorId ? String(creatorId) : undefined,
      }),
    });
  };

  // Mode 1: channel prop provided — just track it, no init needed
  useEffect(() => {
    if (!channelProp) return;
    setChannel(channelProp);
    setIsLoadingChannel(false);
  }, [channelProp]);

  // Mode 2: init from channelId + members
  useEffect(() => {
    if (channelProp) return;
    if (!chatClient || !channelId) { setIsLoadingChannel(false); return; }
    if (!members || !Array.isArray(members)) { setIsLoadingChannel(true); return; }
    if (_lastSynced.get(channelId) === memberIdsKey && channelRef.current) { setIsLoadingChannel(false); return; }
    if (_syncing.has(channelId)) return;

    const init = async () => {
      _syncing.add(channelId);
      try {
        const memberIds = getMemberIds();
        if (!memberIds.length) return;

        const me = String(chatClient.userID);
        if (!memberIds.includes(me)) memberIds.push(me);

        await syncMembersToChannel(memberIds);
        _lastSynced.set(channelId, memberIdsKey);

        if (channelRef.current) { setIsLoadingChannel(false); return; }

        await new Promise(r => setTimeout(r, 500));
        const ch = chatClient.channel(channelType, channelId);
        await ch.watch();
        channelRef.current = ch;
        setChannel(ch);
        setIsLoadingChannel(false);
      } catch (err) {
        console.error('StreamChatComponent init error:', err);
        setIsLoadingChannel(false);
      } finally {
        _syncing.delete(channelId);
      }
    };

    init();

    return () => {
      channelRef.current?.stopWatching().catch(console.error);
      channelRef.current = null;
      _lastSynced.delete(channelId);
    };
  }, [chatClient, channelId, memberIdsKey, channelProp]);

  const chatContainerRef = useRef(null);

  // Patch the Stream Chat input with mobile keyboard attributes matching WhatsApp/Messenger.
  // Stream Chat v14 renders contenteditable="plaintext-only" (not "true"), so we match
  // any contenteditable value. MutationObserver re-patches whenever Stream re-renders.
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const patch = () => {
      const els = container.querySelectorAll('[contenteditable], textarea, input[type="text"]');
      els.forEach(el => {
        if (el.getAttribute('enterkeyhint') === 'send') return;
        el.setAttribute('enterkeyhint', 'send');
        el.setAttribute('inputmode', 'text');
        el.setAttribute('autocorrect', 'on');
        el.setAttribute('autocapitalize', 'sentences');
        el.setAttribute('spellcheck', 'true');
      });
    };

    patch();
    const observer = new MutationObserver(patch);
    observer.observe(container, { childList: true, subtree: true, attributeFilter: ['contenteditable'] });
    return () => observer.disconnect();
  }, [channel]);

  // Lock body scroll when fullscreen so the page behind doesn't scroll
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isFullscreen]);

  // Intercept clicks on links inside the chat — navigate in-app for our own routes
  const handleChatClick = useCallback((e) => {
    const anchor = e.target.closest('a[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href) return;
    const internalPattern = /^(https?:\/\/[^/]*)?(\/marketplace\/listing\/|\/messages|\/profile\/)/;
    if (internalPattern.test(href)) {
      e.preventDefault();
      const path = href.replace(/^https?:\/\/[^/]*/, '');
      navigate(path);
    }
  }, [navigate]);

  if (isConnecting || isLoadingChannel) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-gray-200" style={{ height }}>
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-2 text-blue-500" />
          <p className="text-gray-500 text-sm">Loading chat...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center rounded-2xl border border-gray-200" style={{ height }}>
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  if (!chatClient || !channel) {
    // !chatClient here is normally just the brief moment before connectChat's
    // effect flips isConnecting to true above — not necessarily "logged out".
    return (
      <div className="flex items-center justify-center rounded-2xl border border-gray-200" style={{ height }}>
        <p className="text-sm text-gray-500">{!chatClient ? 'Connecting to chat...' : 'Unable to load channel'}</p>
      </div>
    );
  }

  const containerStyle = fillParent
    ? { flex: 1, minHeight: 0, width: '100%', overflow: 'hidden', position: 'relative', isolation: 'isolate' }
    : isFullscreen
    ? {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 200, isolation: 'isolate',
        display: 'flex', flexDirection: 'column',
        background: headerBg,
      }
    : { height, width: '100%', overflow: 'hidden', position: 'relative', zIndex: 1, isolation: 'isolate' };

  return (
    <div ref={chatContainerRef} style={containerStyle} onClick={handleChatClick}>
      <style>{scopedStyle}</style>
      <style>{scopedLayoutStyle}</style>

      {/* Fullscreen header — only when this component owns the overlay */}
      {isFullscreen && allowFullscreen && (
        <div style={{
          // Not env(safe-area-inset-top): this web view does not report it, and
          // the header was drawn into the camera cutout.
          paddingTop: safeAreaTop(),
          background: headerBg,
          borderBottom: `1px solid ${headerBorder}`,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
            <button
              // Back means back to the conversation list, as it does everywhere
              // else — it used to merely leave full screen, which left you on the
              // same conversation and gave no way out of it. Leaving full screen
              // is now its own control on the right, always present.
              onClick={() => { setIsFullscreen(false); if (onBack) onBack(); }}
              title={onBack ? 'Back to conversations' : 'Exit full screen'}
              aria-label={onBack ? 'Back to conversations' : 'Exit full screen'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, borderRadius: '50%',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: headerText, flexShrink: 0,
              }}
            >
              <ArrowLeft size={20} />
            </button>
            <span style={{
              flex: 1, fontWeight: 600, fontSize: 16, color: headerText,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {channelName || channelProp?.data?.name || 'Chat'}
            </span>
            <button
              onClick={() => setIsFullscreen(false)}
              title="Exit full screen"
              aria-label="Exit full screen"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 36, height: 36, borderRadius: 8,
                background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.15)',
                cursor: 'pointer', color: headerText, flexShrink: 0,
              }}
            >
              <Minimize2 size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Chat content area */}
      <div style={(isFullscreen && allowFullscreen) || fillParent
        ? { flex: 1, minHeight: 0, height: '100%', overflow: 'hidden', position: 'relative', paddingBottom: 'env(safe-area-inset-bottom)' }
        : { height: '100%', position: 'relative' }
      }>
        {/* Hidden theme picker */}
        <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 200, display: 'none' }}>
          <ThemePicker selected={themeId} onChange={setThemeId} />
        </div>

        {/* Expand button (normal mode only, and only if we own full screen) */}
        {!isFullscreen && allowFullscreen && (
          <button
            onClick={() => setIsFullscreen(true)}
            title="Expand to full screen"
            style={{
              position: 'absolute', top: 8, right: 8, zIndex: 200,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32, borderRadius: 8,
              background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)',
              border: '1px solid rgba(255,255,255,0.15)',
              cursor: 'pointer', color: '#fff',
            }}
          >
            <Maximize2 size={14} />
          </button>
        )}

        <Chat client={chatClient} theme={activeTheme.base} customClasses={customClasses}>
          <WithComponents overrides={CHAT_OVERRIDES}>
            <Channel channel={channel} EmojiPicker={EmojiPicker} emojiSearchIndex={SearchIndex}>
              <Window>
                <MessageList />
                {readOnly ? (
                  <div style={{
                    padding: '10px 16px', textAlign: 'center',
                    borderTop: '1px solid rgba(128,128,128,0.2)',
                    fontSize: 13, color: '#6b7280',
                  }}>
                    {readOnlyMessage}
                  </div>
                ) : (
                  <MessageComposer />
                )}
              </Window>
              <Thread />
            </Channel>
          </WithComponents>
        </Chat>
      </div>
    </div>
  );
};

export default StreamChatComponent;
